import React from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { useDeleteDevice } from "../../hooks/useDevices";

// Deletes one or more devices. There's no bulk delete endpoint, so this
// fires one DELETE /device/{id} request per device.
export default function DeleteDevicesDialog({
  deviceIds = [],
  deviceNames = [],
  open,
  handleClose,
}) {
  const deleteDeviceMutation = useDeleteDevice();
  const { showSuccess, showError } = useSnackbar();

  const handleDelete = async () => {
    const results = await Promise.allSettled(
      deviceIds.map((id) => deleteDeviceMutation.mutateAsync(id)),
    );
    const failed = results.filter((r) => r.status === "rejected").length;

    if (failed) {
      showError(
        `Deleted ${deviceIds.length - failed} of ${deviceIds.length} device(s), ${failed} failed`,
      );
    } else {
      showSuccess(
        `Deleted ${deviceIds.length} device${deviceIds.length === 1 ? "" : "s"}`,
      );
    }
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>Delete Devices</DialogTitle>
      <DialogContent>
        <Typography>
          Are you sure you want to permanently delete{" "}
          {deviceIds.length === 1
            ? "this device"
            : `these ${deviceIds.length} devices`}
          ? This cannot be undone.
        </Typography>
        {deviceNames.length > 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {deviceNames.join(", ")}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={handleClose} color="secondary">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleDelete}
          color="error"
          disabled={!deviceIds.length || deleteDeviceMutation.isPending}
        >
          {deleteDeviceMutation.isPending ? "Deleting..." : "Delete"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
